/**
 * Audit trail for lexicon MUTATION (VE-24948 / U5, ADR-003).
 *
 * Required by SEC-R4 with actor, timestamp and before/after values. This is not
 * ordinary bookkeeping: the flat permission model (U5-FR3) lets any org user
 * delete a shared lexicon other people's workflows depend on, which is accepted
 * risk R2, and SEC-R4 records that this trail is "the ONLY control
 * distinguishing legitimate maintenance from destructive change". Compliance
 * driver C2 (CJIS change control) is why that is non-negotiable rather than
 * nice to have.
 *
 * ── The problem this file solves ──────────────────────────────────────────────
 *
 * NFR-S4 says a mutation must not commit unless it is audited. But the SDO write
 * and the audit emission are two independent GraphQL calls to two independent
 * systems with no shared transaction. So partial failure is guaranteed, and the
 * only real question is WHICH LIE THE SYSTEM IS ALLOWED TO TELL:
 *
 *   audit-first, single event    -> a write failure after a successful audit
 *                                  leaves the trail asserting a mutation that
 *                                  never happened. A reviewer cannot tell
 *                                  "deleted" from "tried to delete".
 *   write-first, then compensate -> the compensating restore is ITSELF an
 *                                  un-audited mutation, can fail too, and races
 *                                  other writers under flat permissions.
 *
 * Both are worse than an honest gap, so this implements INTENT-THEN-OUTCOME:
 * emit `intent` (failing closed if it cannot be emitted), perform the guarded
 * write, then emit `outcome`. The trail never asserts something false; at worst
 * it is explicitly indeterminate, and `pairLexiconAuditEvents` resolves that.
 *
 * A useful side effect: an `outcome` with no `intent` is impossible by
 * construction, so the trail can detect its own tampering (SEC-R6).
 *
 * ── SEC-R3 vs SEC-R4 ─────────────────────────────────────────────────────────
 *
 * SEC-R3 says lexicon terms must never be written to APPLICATION LOGS. SEC-R4
 * requires before/after values, which ARE terms. They govern different systems,
 * and the requirements never said so:
 *
 *   operational logs (console, structured app logs, errors, URLs) -> NEVER
 *   audit event store (here)                                      -> REQUIRED
 *   document audit CSV (widely readable)                          -> name only
 *
 * So nothing in this file logs a term, and the CSV renderer filters to `outcome`
 * rows and shows the lexicon NAME only (NFR-S7).
 */

import { LEXICON_AUDIT_LOG_ENABLED } from './auditConfig';
import { GraphQLExecutor } from './services/executor';
import { EMIT_AUDIT_EVENT, EmitAuditEventResponse } from './services/queries';
import { LexiconEntry } from './models';

/**
 * Audit-event application bucket.
 *
 * Reuses video/document `'redact'` rather than a lexicon-specific name for the
 * reason recorded in `documents/services.ts`: the platform only makes events
 * queryable for an application whose registered audit schema indexes the field
 * being filtered on. An ad-hoc application name stores events that emit
 * successfully and can never be read back.
 */
export const LEXICON_AUDIT_APPLICATION = 'redact';

// The settings-page trail's on/off switch, and the reasoning behind its
// current value, live in `auditConfig` — its own module so the ADR-003
// ordering tests can force it on.
export { LEXICON_AUDIT_LOG_ENABLED } from './auditConfig';

export type LexiconMutationKind =
  | 'lexicon.create'
  | 'lexicon.rename'
  | 'lexicon.entries.add'
  | 'lexicon.entries.update'
  | 'lexicon.entries.remove'
  | 'lexicon.settings'
  | 'lexicon.delete'
  | 'lexicon.restore'
  | 'lexicon.purge';

export type LexiconMutationOutcome = 'committed' | 'conflict' | 'failed';

/** Identity and scope the emitter cannot derive for itself. */
export interface LexiconAuditContext {
  /**
   * Actor, taken from the authenticated session by the caller — NEVER from a
   * client-supplied field (NFR-S6).
   */
  readonly actor: string;
  /** Org id, used by the S-1 key seam below. */
  readonly organizationId: string;
  /** Injected so tests are deterministic and do not depend on wall-clock time. */
  readonly now: () => string;
}

/**
 * Bounded before/after payload.
 *
 * BR-36: changed fields plus the specific entries touched, capped. Embedding a
 * whole 5,000-entry list would put roughly half a megabyte into an audit payload
 * on every edit, against a >= 6-year retention (NFR-U5-1).
 */
export const AUDIT_ENTRY_SAMPLE_CAP = 50;

export interface LexiconAuditSnapshot {
  readonly name?: string;
  readonly entryCount?: number;
  /**
   * Library flags, on a `lexicon.settings` event.
   *
   * Recorded because turning a lexicon off is the one change that stops it
   * finding anything without removing it — indistinguishable, six months later,
   * from a lexicon that simply never matched, unless the trail says who turned
   * it off and when.
   */
  readonly enabled?: boolean;
  readonly autoApply?: boolean;
  /** At most `AUDIT_ENTRY_SAMPLE_CAP` of the entries this change touched. */
  readonly entries?: readonly LexiconEntry[];
  /** How many touched entries were omitted by the cap. */
  readonly entriesTruncated?: number;
}

export interface LexiconAuditEvent {
  readonly phase: 'intent' | 'outcome';
  /**
   * Idempotency key, generated ONCE per user action and reused across retries of
   * that action, so a retry after an ambiguous failure cannot read as two
   * separate mutations.
   */
  readonly operationId: string;
  readonly kind: LexiconMutationKind;
  readonly entityType: 'lexicon';
  readonly lexiconId: string;
  readonly actor: string;
  readonly timestamp: string;
  readonly revisionFrom?: number;
  readonly revisionTo?: number;
  readonly before?: LexiconAuditSnapshot;
  readonly after?: LexiconAuditSnapshot;
  readonly result?: LexiconMutationOutcome;
  /** Human-readable summary, used as the ACTION column. Never contains a term. */
  readonly action: string;
}

/**
 * Caps an entry list for an audit payload (BR-36).
 *
 * Exported so the cap is testable independently of emission — the property that
 * matters is that a large edit produces a bounded payload with an honest count
 * of what was dropped, not that some particular call site remembered to cap.
 */
export function boundEntrySample(entries: readonly LexiconEntry[]): {
  readonly entries: readonly LexiconEntry[];
  readonly entriesTruncated?: number;
} {
  if (entries.length <= AUDIT_ENTRY_SAMPLE_CAP) {
    return { entries };
  }
  return {
    entries: entries.slice(0, AUDIT_ENTRY_SAMPLE_CAP),
    entriesTruncated: entries.length - AUDIT_ENTRY_SAMPLE_CAP,
  };
}

/**
 * The S-1 seam.
 *
 * ⚠️ `tdoId` IS THE WRONG TERM AND THIS IS KNOWN. A lexicon mutation is
 * ORG-scoped; it has no TDO, and nothing here is about a document. The
 * organization id is being put in a field named `tdoId` purely because that is
 * the one term the audit query is known to index, so it was the only way to
 * make an org-scoped event reachable at all.
 *
 * That choice has a consequence the rest of the app then inherited: because
 * these events occupy the `tdoId` key space with a value that is not a tdoId,
 * they are invisible to every reader that legitimately queries by document —
 * which is every reader there is. `uploadDocumentAuditLog` asks for
 * `terms: [{ tdoId: <document id> }]`, so it can never return one of these.
 * That is the direct reason `LEXICON_AUDIT_LOG_ENABLED` is false.
 *
 * DO NOT build a reader on top of this key. The fix is to find the right term
 * — S-1 was meant to confirm whether the registered audit schema can index
 * `entityType`/`lexiconId` — and change this function. Everything else about
 * ADR-003's ordering is correct either way, so this is the only place that
 * needs to move.
 *
 * NFR-S5 is NOT satisfied until a test proves these events come back through
 * the real query path. That test is the release gate — emission alone does not
 * satisfy SEC-R4, because an unreadable trail and a missing trail are
 * indistinguishable to a reviewer, and today they are the same thing.
 */
export function auditEventKey(
  ctx: LexiconAuditContext
): Record<string, string> {
  return { tdoId: ctx.organizationId };
}

/** Builds the ACTION summary. Deliberately never includes a term. */
function describeAction(
  kind: LexiconMutationKind,
  lexiconName: string,
  detail?: { readonly newName?: string; readonly entryCount?: number }
): string {
  switch (kind) {
    case 'lexicon.create':
      return `Created lexicon "${lexiconName}"`;
    case 'lexicon.rename':
      return `Renamed lexicon "${lexiconName}" to "${detail?.newName ?? ''}"`;
    case 'lexicon.entries.add':
      return `Added ${detail?.entryCount ?? 0} entries to lexicon "${lexiconName}"`;
    case 'lexicon.entries.update':
      // "Changed", not "deleted and added". An edit that reads as a deletion
      // plus an unrelated addition misdescribes the record, and this record is
      // the disclosure record — see the module header on why that matters more
      // than the extra mutation kind costs.
      return `Changed ${detail?.entryCount ?? 0} entries in lexicon "${lexiconName}"`;
    case 'lexicon.entries.remove':
      return `Removed ${detail?.entryCount ?? 0} entries from lexicon "${lexiconName}"`;
    case 'lexicon.settings':
      return `Changed settings on lexicon "${lexiconName}"`;
    case 'lexicon.delete':
      return `Deleted lexicon "${lexiconName}"`;
    case 'lexicon.restore':
      return `Restored lexicon "${lexiconName}"`;
    // "Permanently deleted", never just "deleted": these two events are the
    // recoverable and the irreversible act, and a reviewer reading the trail
    // years later has only this sentence to tell them apart.
    case 'lexicon.purge':
      return `Permanently deleted lexicon "${lexiconName}"`;
    default: {
      // Exhaustiveness guard: adding a kind without a description is a compile
      // error rather than an event that silently says nothing.
      const exhaustive: never = kind;
      return exhaustive;
    }
  }
}

export interface LexiconIntentInput {
  readonly operationId: string;
  readonly kind: LexiconMutationKind;
  readonly lexiconId: string;
  readonly lexiconName: string;
  readonly revisionFrom?: number;
  readonly before?: LexiconAuditSnapshot;
  readonly after?: LexiconAuditSnapshot;
  readonly detail?: { readonly newName?: string; readonly entryCount?: number };
}

async function emit(
  exec: GraphQLExecutor,
  ctx: LexiconAuditContext,
  event: LexiconAuditEvent
): Promise<void> {
  const response = await exec<EmitAuditEventResponse>({
    query: EMIT_AUDIT_EVENT,
    variables: {
      application: LEXICON_AUDIT_APPLICATION,
      payload: { ...auditEventKey(ctx), ...event, userName: ctx.actor },
    },
    operationName: 'emitLexiconAuditEvent',
  });
  /**
   * THE RESPONSE IS CHECKED, and this is the one call where skipping it
   * matters most.
   *
   * `throwOnError` covers a GraphQL `errors` array, but NOT a 200 carrying
   * `data: { emitAuditEvent: null }`. That resolves normally, so a discarded
   * return value let `emitLexiconIntent` fabricate an event object and
   * `performAuditedMutation` go on to the write — NFR-S4 failing OPEN on
   * exactly the case SEC-R4 calls the only control distinguishing legitimate
   * maintenance from destructive change.
   *
   * Every other write in this module already defends against this
   * (`createLexicon` throws on a null `createStructuredData`); the security
   * control was the one that did not.
   */
  if (!response?.emitAuditEvent?.id) {
    throw new Error('Audit event was not recorded.');
  }
}

/**
 * Emits the `intent` event.
 *
 * The caller MUST treat a rejection here as fatal and NOT perform the write —
 * that is what makes NFR-S4 structural rather than a policy someone has to
 * remember. `performAuditedMutation` below is the intended entry point.
 */
export async function emitLexiconIntent(
  exec: GraphQLExecutor,
  ctx: LexiconAuditContext,
  input: LexiconIntentInput
): Promise<LexiconAuditEvent> {
  const event: LexiconAuditEvent = {
    phase: 'intent',
    operationId: input.operationId,
    kind: input.kind,
    entityType: 'lexicon',
    lexiconId: input.lexiconId,
    actor: ctx.actor,
    timestamp: ctx.now(),
    ...(input.revisionFrom !== undefined
      ? { revisionFrom: input.revisionFrom }
      : {}),
    ...(input.before ? { before: input.before } : {}),
    ...(input.after ? { after: input.after } : {}),
    action: describeAction(input.kind, input.lexiconName, input.detail),
  };
  await emit(exec, ctx, event);
  return event;
}

/** Emits the `outcome` event. A failure here leaves an honest, resolvable gap. */
export async function emitLexiconOutcome(
  exec: GraphQLExecutor,
  ctx: LexiconAuditContext,
  intent: LexiconAuditEvent,
  result: LexiconMutationOutcome,
  revisionTo?: number
): Promise<void> {
  await emit(exec, ctx, {
    ...intent,
    phase: 'outcome',
    timestamp: ctx.now(),
    result,
    ...(revisionTo !== undefined ? { revisionTo } : {}),
  });
}

export type PairedAuditVerdict =
  | 'happened'
  | 'did-not-happen'
  | 'indeterminate'
  | 'tampered';

export interface PairedAuditRecord {
  readonly operationId: string;
  readonly kind: LexiconMutationKind;
  readonly lexiconId: string;
  readonly actor: string;
  readonly timestamp: string;
  readonly verdict: PairedAuditVerdict;
  readonly revisionFrom?: number;
  readonly revisionTo?: number;
}

/**
 * Pairs `intent` and `outcome` events into one verdict per operation.
 *
 * Shipped as a shared helper rather than described in a document because it is
 * the largest hidden cost of ADR-003: EVERY consumer of this trail must pair
 * events or it will misread an intent as a completed mutation. Prose does not
 * prevent that; a function does.
 *
 *   intent + outcome(committed)         -> happened
 *   intent + outcome(conflict|failed)   -> did-not-happen
 *   intent alone                        -> indeterminate; resolve by comparing
 *                                          `revisionFrom` with the lexicon's
 *                                          current revision
 *   outcome alone                       -> tampered (impossible by construction)
 */
export function pairLexiconAuditEvents(
  events: readonly LexiconAuditEvent[]
): readonly PairedAuditRecord[] {
  const intents = new Map<string, LexiconAuditEvent>();
  const outcomes = new Map<string, LexiconAuditEvent>();
  for (const event of events) {
    if (event.entityType !== 'lexicon') {
      continue;
    }
    (event.phase === 'intent' ? intents : outcomes).set(
      event.operationId,
      event
    );
  }

  const records: PairedAuditRecord[] = [];

  for (const [operationId, intent] of intents) {
    const outcome = outcomes.get(operationId);
    const verdict: PairedAuditVerdict = !outcome
      ? 'indeterminate'
      : outcome.result === 'committed'
        ? 'happened'
        : 'did-not-happen';
    records.push({
      operationId,
      kind: intent.kind,
      lexiconId: intent.lexiconId,
      actor: intent.actor,
      timestamp: intent.timestamp,
      verdict,
      ...(intent.revisionFrom !== undefined
        ? { revisionFrom: intent.revisionFrom }
        : {}),
      ...(outcome?.revisionTo !== undefined
        ? { revisionTo: outcome.revisionTo }
        : {}),
    });
  }

  for (const [operationId, outcome] of outcomes) {
    if (intents.has(operationId)) {
      continue;
    }
    records.push({
      operationId,
      kind: outcome.kind,
      lexiconId: outcome.lexiconId,
      actor: outcome.actor,
      timestamp: outcome.timestamp,
      verdict: 'tampered',
    });
  }

  return records;
}

/** Raised when the `intent` event could not be emitted, so nothing was written. */
export class LexiconAuditBlockedError extends Error {
  constructor(cause: unknown) {
    super(
      'This change was not applied because it could not be recorded in the ' +
        'audit log. Please try again.'
    );
    this.name = 'LexiconAuditBlockedError';
    this.cause = cause;
  }
}

/** Raised when the write succeeded but its `outcome` event could not be emitted. */
export class LexiconAuditIncompleteError extends Error {
  constructor(
    public readonly operationId: string,
    cause: unknown
  ) {
    super(
      'The change was applied, but the audit log could not record its ' +
        'completion. It is recorded as incomplete and should be verified.'
    );
    this.name = 'LexiconAuditIncompleteError';
    this.cause = cause;
  }
}

/**
 * Runs a mutation under the ADR-003 sequence. This is the only sanctioned way to
 * mutate a lexicon.
 *
 * Fail-closed is asymmetric on purpose: blocking costs availability on a
 * SETTINGS PAGE, while failing open costs a permanent unattributable gap in the
 * only control over a shared resource every org user can delete. Document
 * review, upload, redaction and export are all unaffected by an audit outage.
 */
export async function performAuditedMutation<T>(
  exec: GraphQLExecutor,
  ctx: LexiconAuditContext,
  input: LexiconIntentInput,
  write: () => Promise<{ readonly result: T; readonly revisionTo?: number }>
): Promise<T> {
  /**
   * OFF — do the write, audit nothing. See `LEXICON_AUDIT_LOG_ENABLED`.
   *
   * Placed ABOVE the fail-closed guards deliberately. Those guards refuse the
   * mutation when an event could not be attributed or indexed, which is right
   * when a trail exists and absurd when it does not — a missing actor would
   * otherwise block a lexicon edit to protect a record nobody writes.
   *
   * The write is returned unchanged, so callers cannot tell the difference and
   * no `LexiconAuditBlockedError` / `LexiconAuditIncompleteError` can be
   * raised while this is false. That is what keeps the settings page working
   * identically with the trail off.
   */
  if (!LEXICON_AUDIT_LOG_ENABLED) {
    return (await write()).result;
  }

  // An event that cannot be INDEXED is not an audit record: `auditEventKey`
  // puts `organizationId` in the only queryable term, so emitting with it empty
  // produces a write nobody can retrieve. NFR-S4's fail-closed has to cover
  // "unrecordable", not just "rejected" — otherwise the mutation proceeds and
  // the trail silently does not exist (review finding 1).
  if (!ctx.organizationId) {
    throw new LexiconAuditBlockedError(
      new Error('Refusing to audit a lexicon mutation with no organization id')
    );
  }
  /**
   * ...and an event that cannot be ATTRIBUTED is not one either.
   *
   * `actor` defaults to `''` when `selectUser` has not hydrated, which is
   * reachable if a mutation fires early in the session. The record then says a
   * lexicon was deleted and does not say by whom — and naming who made a
   * change is the whole purpose of the CJIS change-control driver (C2) this
   * trail exists to serve. Same argument as the org id above, same fail-closed
   * answer: unattributable belongs in the same bucket as unrecordable.
   */
  if (!ctx.actor) {
    throw new LexiconAuditBlockedError(
      new Error('Refusing to audit a lexicon mutation with no actor')
    );
  }

  let intent: LexiconAuditEvent;
  try {
    intent = await emitLexiconIntent(exec, ctx, input);
  } catch (error) {
    // NFR-S4 — no intent, no write. Nothing has been touched.
    throw new LexiconAuditBlockedError(error);
  }

  let written: { readonly result: T; readonly revisionTo?: number };
  try {
    written = await write();
  } catch (error) {
    // Record that it did not happen, then surface the original failure. A
    // best-effort catch here: if this emission also fails there is nothing
    // further to do, and the trail already shows an unresolved intent.
    try {
      await emitLexiconOutcome(exec, ctx, intent, 'failed');
    } catch {
      // Intentionally swallowed — see above. The intent-without-outcome state
      // is the documented, resolvable representation of exactly this case.
    }
    throw error;
  }

  try {
    await emitLexiconOutcome(
      exec,
      ctx,
      intent,
      'committed',
      written.revisionTo
    );
  } catch (error) {
    throw new LexiconAuditIncompleteError(intent.operationId, error);
  }

  return written.result;
}
